import { ErrorMessages, RequestErrorMessages } from 'constants/errors';
import { ResponseError } from 'types/request';

export interface IErrorHandlerOptions<T, K> {
  defaultErrorReturn?: T;
  errorMessage?: string;
  errors?: Record<string, string>;
  allowErrorBodyMessage?: boolean;
  modifyResponse?: (initialData: T) => K;
}

export interface IErrorHandlerResponse<T> {
  data: T;
  success: boolean;
  errorMessage?: string;
  error?: ResponseError;
}

const asyncErrorHandler = async <T, K>(
  callback: () => Promise<T>,
  options?: IErrorHandlerOptions<T, K>,
): Promise<IErrorHandlerResponse<T | K>> => {
  const {
    defaultErrorReturn = null,
    errorMessage = ErrorMessages.COMMON.UNKNOWN,
    errors = {},
    allowErrorBodyMessage,
    modifyResponse = (data: T) => data,
  } = options || {};

  try {
    const data = await callback();

    const modifiedData = modifyResponse(data);

    return { data: modifiedData, success: true };
  } catch (error) {
    if (!error?.body?.key && !allowErrorBodyMessage) {
      return { data: defaultErrorReturn, success: false, errorMessage, error };
    }

    const requestErrors = { ...RequestErrorMessages, ...errors };

    const errorBodyMessage = allowErrorBodyMessage ? error?.body?.message || error?.message : null;
    const message = errorBodyMessage || requestErrors[error.body.key] || errorMessage;

    return { data: defaultErrorReturn, success: false, errorMessage: message, error };
  }
};

export default asyncErrorHandler;
